import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import BitLogo from 'client/components/CampaignPage/components/BitLogo/BitLogo';
import CashAppLogo from 'client/components/CampaignPage/components/CashAppLogo/CashAppLogo';
import ChariotDafPayLogo from '../../components/ChariotDafPayLogo/ChariotDafPayLogo';
import DonorsFundLogo from 'client/components/CampaignPage/components/DonorsFundLogo/DonorsFundLogo';
import GoogleApplePayLogo from 'client/components/CampaignPage/components/GoogleApplePayIcon/GoogleApplePayIcon';
import MatbiaLogo from 'client/components/CampaignPage/components/MatbiaLogo/MatbiaLogo';
import OjcFundLogo from 'client/components/CampaignPage/components/OjcFundLogo/OjcFundLogo';
import PaypalLogo from 'client/components/CampaignPage/components/PaypalIcon/PaypalIcon';
import PledgerLogo from 'client/components/CampaignPage/components/PledgerLogo/PledgerLogo';
import RadioGroupTab from 'client/components/CampaignPage/components/RadioGroupTab/RadioGroupTab';
import RadioGroupTabs from 'client/components/CampaignPage/components/RadioGroupTabs/RadioGroupTabs';
import { usePaymentRequest } from 'client/components/CampaignPage/features/checkout/StripeWalletInfo';
import PaymentMethodIcon from 'client/components/Icons/PaymentMethods.js';
import { PAYMENT_GATEWAYS, PAYMENT_METHODS } from 'common/constants';
import { paymentMethodLabelProp } from 'common/helpers/paymentMethods';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    selectEnabledPaymentMethods,
    selectPaymentGateway,
} from './checkoutSlice';
import classNames from 'classnames';
import IdealLogo from '../../components/IdealLogo/IdealLogo';

const useStyles = makeStyles(({ palette, spacing }) => ({
    root: {
        '@media (max-width: 600px)': {
            flexWrap: 'wrap',

            '& input + div': {
                padding: '5px 20px',
            },
        },
    },
    bitPayment: {
        'input:checked + &': {
            background: `linear-gradient(180deg, ${palette.brands.bit.primary}, ${palette.brands.bit.secondary})`,
        },
    },
    paypal: {
        'input:checked + &': {
            background: palette.secondary.main,
        },
    },
    checkedBlack: {
        'input:checked + &': {
            background: palette.common.black,
        },
    },
    matbia: {
        'input:checked + &': {
            background: palette.brands.matbia,
        },
    },
    donorsFund: {
        padding: '4px 20px',
    },
    iconLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    roundPaymentMethodIcon: {
        marginInlineEnd: spacing(0.75),
        minWidth: 26,
        minHeight: 26,
    },
    breakSpaces: {
        whiteSpace: 'break-spaces',
    },
}));
function PaymentMethodSelector(props) {
    const { onChange, value } = props;
    const classes = useStyles();
    const { palette } = useTheme();
    const enabledPaymentMethods = useSelector(selectEnabledPaymentMethods);
    const paymentGateway = useSelector(selectPaymentGateway);

    const { canMakePayment } = usePaymentRequest({
        isDisabled: ![
            PAYMENT_METHODS.GOOGLE_PAY,
            PAYMENT_METHODS.APPLE_PAY,
        ].some(i => enabledPaymentMethods.includes(i)),
    });

    if (!enabledPaymentMethods.length) {
        return null;
    }

    const getPaymentMethodContent = (paymentMethod, isDisabled) => {
        switch (paymentMethod) {
            case PAYMENT_METHODS.BIT:
                return <BitLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.PAYPAL:
                return <PaypalLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.APPLE_PAY:
            case PAYMENT_METHODS.GOOGLE_PAY:
                return (
                    <GoogleApplePayLogo
                        paymentMethod={paymentMethod}
                        isChecked={paymentMethod === value}
                        isDisabled={isDisabled}
                    />
                );
            case PAYMENT_METHODS.CASHAPP:
                return <CashAppLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.IDEAL:
                return <IdealLogo />;
            case PAYMENT_METHODS.PLEDGER:
                return <PledgerLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.MATBIA:
                return <MatbiaLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.CHARIOT:
                return <ChariotDafPayLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.OJCFUND:
                return <OjcFundLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.DONORSFUND:
                return <DonorsFundLogo checked={paymentMethod === value} />;
            case PAYMENT_METHODS.AAC:
            case PAYMENT_METHODS.ASSERBISHVIL:
            case PAYMENT_METHODS.BELZ:
            case PAYMENT_METHODS.BROOM:
            case PAYMENT_METHODS.CARD:
            case PAYMENT_METHODS.CMZ:
            case PAYMENT_METHODS.KOLYOM:
            case PAYMENT_METHODS.MHTRUST:
            case PAYMENT_METHODS.YADSHLOMO:
            case PAYMENT_METHODS.ZOREYATZEDOKOS:
                return (
                    <div
                        className={classNames(classes.iconLabel, {
                            [classes.breakSpaces]:
                                paymentMethod ===
                                PAYMENT_GATEWAYS.ZOREYATZEDOKOS,
                        })}
                    >
                        <PaymentMethodIcon
                            width="26"
                            height="26"
                            paymentMethod={paymentMethod}
                            className={classes.roundPaymentMethodIcon}
                            fill={palette.background.paper}
                        />
                        <FormattedMessage
                            id={paymentMethodLabelProp(paymentMethod)}
                            defaultMessage={paymentMethod}
                        />
                    </div>
                );
            default:
                return (
                    <FormattedMessage
                        id={paymentMethodLabelProp(paymentMethod)}
                        defaultMessage={paymentMethod}
                    />
                );
        }
    };

    const getClassName = paymentMethod => {
        switch (paymentMethod) {
            case PAYMENT_METHODS.PAYPAL:
                return classes.paypal;
            case PAYMENT_METHODS.BIT:
                return classes.bitPayment;
            case PAYMENT_METHODS.MATBIA:
                return classes.matbia;
            case PAYMENT_METHODS.DONORSFUND:
                return classNames(classes.checkedBlack, classes.donorsFund);
            case PAYMENT_METHODS.PLEDGER:
            case PAYMENT_METHODS.APPLE_PAY:
            case PAYMENT_METHODS.GOOGLE_PAY:
            case PAYMENT_METHODS.OJCFUND:
                return classes.checkedBlack;
            default:
                return null;
        }
    };

    const isDisabled = paymentMethod => {
        switch (paymentMethod) {
            case PAYMENT_METHODS.APPLE_PAY:
                return (
                    paymentGateway === PAYMENT_GATEWAYS.STRIPE &&
                    !canMakePayment?.applePay
                );
            case PAYMENT_METHODS.GOOGLE_PAY:
                return (
                    paymentGateway === PAYMENT_GATEWAYS.STRIPE &&
                    !canMakePayment?.googlePay
                );
            default:
                return false;
        }
    };

    return (
        <RadioGroupTabs className={classes.root}>
            {enabledPaymentMethods.map(paymentMethod => (
                <RadioGroupTab
                    name="paymentMethod"
                    key={paymentMethod}
                    onChange={onChange(paymentMethod)}
                    checked={paymentMethod === value}
                    buttonClassName={getClassName(paymentMethod)}
                    disabled={isDisabled(paymentMethod)}
                >
                    {getPaymentMethodContent(
                        paymentMethod,
                        isDisabled(paymentMethod),
                    )}
                </RadioGroupTab>
            ))}
        </RadioGroupTabs>
    );
}

PaymentMethodSelector.propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.any,
};

export default PaymentMethodSelector;
